import { SET_TOKEN, SET_RENEW_STARTED, SET_AUTH_FINISHED } from '@/store/mutation.types';
import { userManagerFactory, silentSignIn } from '@justeat/f-consumer-oidc';

let userManager = {};

const getAuthSettings = settings => {
    const authSettings = settings.auth;
    const silentRedirectUri = `${document.location.protocol}//${document.location.host}${authSettings.silentRedirectPath}`;

    return {
        silent_redirect_uri: silentRedirectUri, // eslint-disable-line camelcase
        automaticSilentRenew: false, // Only the call that creates the userManager will have it set to true
        retryInterval: authSettings.retryInterval || 5000,
        silentRequestTimeout: authSettings.retryInterval || 10000
    };
};

const isUserExpired = async () => {
    const user = await userManager.getUser();

    return !user || user.expired || user.expired === undefined;
};

const signInIfUserExpired = async (authSettings, state, commit, $cookies) => {
    const isExpired = await isUserExpired();
    if (!isExpired) {
        return;
    }

    // Remove existing token to prevent header size from causing the renewal to fail
    $cookies.remove('je-oidc');

    // No token found or token expired, getting new token
    const token = await silentSignIn(window.Oidc, authSettings, true);
    commit(SET_TOKEN, token);
};

const registerSilentRenew = async (settings, state, commit, $cookies) => {
    const authSettings = getAuthSettings(settings);
    const token = await silentSignIn(window.Oidc, authSettings, false);
    userManager = userManagerFactory(window.Oidc, { ...authSettings, automaticSilentRenew: true });
    const isExpired = await isUserExpired();

    if (!isExpired) {
        commit(SET_TOKEN, token);
    } else {
        await signInIfUserExpired(authSettings, state, commit, $cookies);
    }

    userManager.events.addUserLoaded(async () => {
        // Token refreshed
        const newToken = await silentSignIn(window.Oidc, authSettings, false);
        commit(SET_TOKEN, newToken);
    });

    userManager.events.addSilentRenewError(() => {
        // Silent renew error while token is still valid, will try again in retryInterval
        // Event will be raised again if restarting renew in setTimeout fails again
        setTimeout(() => {
            userManager.stopSilentRenew();
            userManager.startSilentRenew();
        }, authSettings.retryInterval);
    });

    userManager.events.addAccessTokenExpired(async () => {
        // Token expired
        setTimeout(async () => {
            try {
                await signInIfUserExpired(authSettings, state, commit, $cookies);
            } catch {
                // Error while trying to renew token, will try again in retryInterval
            }
        }, authSettings.retryInterval);
    });
};

export default {
    state: () => ({
        authToken: null,
        isAuthFinished: false,
        hasSilentRenewStarted: false
    }),

    actions: {
        async requestSilentRenew ({ state, commit }, settings) {
            if (state.hasSilentRenewStarted) {
                // Silent renew should be started only once
                return;
            }
            commit(SET_RENEW_STARTED, true);

            try {
                await registerSilentRenew(settings, state, commit, this.$cookies);
            } finally {
                commit(SET_AUTH_FINISHED, true);
            }
        },
        exchangeOtac: async (_, { otac, settings }) => {
            const authSettings = getAuthSettings(settings);
            authSettings.acr_values = `otac:${otac}`; // eslint-disable-line camelcase

            const token = await silentSignIn(window.Oidc, authSettings, true);

            // Do not persist the token to prevent app from displaying the logged in state
            return token;
        }
    },

    mutations: {
        [SET_TOKEN]: (state, authToken) => {
            state.authToken = authToken;
        },
        [SET_AUTH_FINISHED]: (state, isAuthFinished) => {
            state.isAuthFinished = isAuthFinished;
        },
        [SET_RENEW_STARTED]: (state, hasSilentRenewStarted) => {
            state.hasSilentRenewStarted = hasSilentRenewStarted;
        }
    }
};
